import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Mail, FileText, Mic, Calendar, Image, CheckCircle2, AlertCircle, Heart, Download, Server, Wrench, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { products: Package, spares: Package, spare_orders: Package, new_orders: Package, leads: Mail, new_leads: Mail, blogs: FileText, podcasts: Mic, events: Calendar, gallery: Image };
const LABELS = { products: "Products", spares: "Spares", spare_orders: "Spare Orders", new_orders: "New Orders", leads: "Total Leads", new_leads: "New Leads", blogs: "Blog Posts", podcasts: "Podcast Episodes", events: "Events", gallery: "Gallery Items" };

// Definition of "completeness" checks — each row in Site Health.
function buildChecks(s = {}) {
  const has = (v) => Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
  return [
    { key: "logo", label: "Logo uploaded", ok: has(s.logo_url), fix: "/admin/settings", section: "Basics" },
    { key: "phone", label: "Phone number set", ok: has(s.phone) && s.phone !== "+91 XXXXXXXXXX", fix: "/admin/settings", section: "Contact / Footer" },
    { key: "email", label: "Email set", ok: has(s.email) && s.email.includes("@"), fix: "/admin/settings", section: "Contact / Footer" },
    { key: "address", label: "Address filled", ok: has(s.address), fix: "/admin/settings", section: "Contact / Footer" },
    { key: "hero_slides", label: "Hero carousel has at least one image", ok: has(s.hero_slides), fix: "/admin/settings", section: "Home — Hero" },
    { key: "about_image", label: "About-band photo uploaded", ok: has(s.about_image), fix: "/admin/settings", section: "Home — About Band" },
    { key: "about_story_image", label: "About page 'Our Story' photo uploaded", ok: has(s.about_story_image), fix: "/admin/settings", section: "About — Our Story" },
    { key: "corporate_video_url", label: "Corporate video added (optional)", ok: has(s.corporate_video_url), warn: true, fix: "/admin/settings", section: "Home — Corporate Video" },
    { key: "engineers_image", label: "Engineers page photo uploaded", ok: has(s.engineers_image), fix: "/admin/settings", section: "Engineers Page" },
    { key: "testimonials", label: "At least 1 testimonial published", ok: false, dynamic: "testimonials", fix: "/admin/testimonials", section: "Customer Stories" },
    { key: "team", label: "At least 1 team member added", ok: false, dynamic: "team", fix: "/admin/team", section: "Management Team" },
    { key: "products", label: "At least 1 product added", ok: false, dynamic: "products", fix: "/admin/products", section: "Products" },
    { key: "social", label: "At least 1 social link (LinkedIn / Insta / FB)", ok: has(s.linkedin) || has(s.instagram) || has(s.facebook), fix: "/admin/settings", section: "Social Links" },
    { key: "announcement", label: "Active announcement (optional)", ok: false, dynamic: "announcement", warn: true, fix: "/admin/announcements", section: "Announcements" },
  ];
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [counts, setCounts] = useState({});
  const [migrating, setMigrating] = useState(false);
  const [migrateReport, setMigrateReport] = useState(null);

  async function runMigration() {
    if (migrating) return;
    setMigrating(true);
    setMigrateReport(null);
    try {
      const { data } = await api.post("/admin/_debug/migrate");
      setMigrateReport(data);
      const m = data.migrations || {};
      const failed = (m.failed || []).length;
      if (failed > 0) {
        toast.error(`Migration finished with ${failed} failure(s). See report below.`);
      } else {
        const applied = (m.applied || []).length;
        const skipped = (m.skipped || []).length;
        toast.success(`Schema is in sync. ${applied} change(s) applied, ${skipped} already up-to-date.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed. Check server logs.");
    } finally {
      setMigrating(false);
    }
  }

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    Promise.all([
      api.get("/testimonials").then((r) => r.data.length).catch(() => 0),
      api.get("/team").then((r) => r.data.length).catch(() => 0),
      api.get("/products").then((r) => r.data.length).catch(() => 0),
      api.get("/announcement").then((r) => (r.data ? 1 : 0)).catch(() => 0),
    ]).then(([t, tm, p, a]) => setCounts({ testimonials: t, team: tm, products: p, announcement: a }));
  }, []);

  const checks = buildChecks(settings || {}).map((c) => {
    if (c.dynamic) c = { ...c, ok: (counts[c.dynamic] || 0) > 0 };
    return c;
  });
  const required = checks.filter((c) => !c.warn);
  const passedRequired = required.filter((c) => c.ok).length;
  const score = required.length === 0 ? 100 : Math.round((passedRequired / required.length) * 100);
  const scoreColor = score >= 80 ? "#16a34a" : score >= 50 ? "#E65C00" : "#dc2626";

  return (
    <div data-testid="admin-dashboard">
      <h1 className="heading-xl text-3xl">DASHBOARD</h1>
      <p className="text-gray-600 mt-1 text-sm">Welcome to Tara Autohub Control Room.</p>

      {/* SITE HEALTH WIDGET */}
      <div className="mt-8 bg-white border-l-4 border-[#E65C00] border border-gray-200 p-6" data-testid="site-health-widget">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5" style={{ color: scoreColor }} />
              <h2 className="font-heading font-black uppercase text-xl">Site Health</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">{passedRequired}/{required.length} required items complete · How polished your live site is.</p>
          </div>
          <div className="text-right">
            <div className="font-heading font-black text-4xl" style={{ color: scoreColor }} data-testid="site-health-score">{score}%</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Completeness</div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-100 mb-5 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${score}%`, background: scoreColor }} />
        </div>
        {/* Checklist */}
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {checks.map((c) => (
            <div key={c.key} className="flex items-start gap-2 text-sm" data-testid={`health-${c.key}`}>
              {c.ok
                ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                : <AlertCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.warn ? "text-gray-400" : "text-red-500"}`} />}
              <div className="min-w-0">
                <div className={`${c.ok ? "text-gray-700" : c.warn ? "text-gray-500" : "text-gray-900 font-semibold"}`}>{c.label}</div>
                {!c.ok && (
                  <Link to={c.fix} className="text-xs text-[#E65C00] hover:underline">
                    Fix in {c.section} →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <h2 className="heading-xl text-lg mt-10">CONTENT COUNTS</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
        {stats ? Object.entries(stats).map(([k, v]) => {
          const Icon = ICONS[k] || Package;
          return (
            <div key={k} className="bg-white border border-gray-200 p-5" data-testid={`stat-${k}`}>
              <div className="flex justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{LABELS[k] || k}</div>
                <Icon className="w-4 h-4 text-[#E65C00]" />
              </div>
              <div className="font-heading font-black text-3xl mt-3">{v}</div>
            </div>
          );
        }) : Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-24" />)}
      </div>

      <div className="mt-10 bg-[#1A1A1A] text-white p-7 border-l-4 border-[#E65C00]">
        <div className="font-heading font-black uppercase">Quick Actions</div>
        <div className="text-sm text-gray-400 mt-2">Use the sidebar to manage products, content, leads, announcements and site settings. All edits go live instantly.</div>
      </div>

      {/* DATABASE SELF-HEAL / MIGRATION TILE */}
      <div className="mt-6 bg-white border-2 border-[#1A1A1A] p-6 sm:p-7" data-testid="migration-tile">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="flex items-start gap-3 max-w-2xl">
            <div className="w-12 h-12 bg-[#1A1A1A] text-[#FFCC00] flex items-center justify-center shrink-0">
              <Wrench className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-[#1A1A1A] text-xl tracking-tight">Database Self-Heal</div>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed">
                If you&apos;ve recently imported an older <code className="bg-gray-100 px-1.5 py-0.5 text-xs">database.sql</code> backup
                and admin pages throw &ldquo;Unknown column&rdquo; errors, click below. It adds any missing columns
                (<code className="bg-gray-100 px-1 text-xs">visible</code>, <code className="bg-gray-100 px-1 text-xs">brochure_url</code>,
                <code className="bg-gray-100 px-1 text-xs">images</code>, <code className="bg-gray-100 px-1 text-xs">video_url</code>, …)
                and re-syncs your admin password from the server <code className="bg-gray-100 px-1 text-xs">.env</code>.
                Safe to run anytime — idempotent.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={runMigration}
            disabled={migrating}
            className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#E65C00] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 font-heading font-black uppercase tracking-widest text-xs transition-colors"
            data-testid="run-migration-btn"
          >
            {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {migrating ? "Running…" : "Apply Migration"}
          </button>
        </div>

        {migrateReport && (
          <div className="mt-5 border-t border-gray-200 pt-4 text-xs font-mono" data-testid="migration-report">
            <div className="text-gray-500 uppercase tracking-widest font-heading mb-2">Report ({migrateReport.backend || "node-mysql"})</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <div className="font-bold text-green-700 mb-1">Applied ({(migrateReport.migrations?.applied || []).length})</div>
                <ul className="space-y-0.5">
                  {(migrateReport.migrations?.applied || []).map((s) => <li key={s} className="text-green-700">+ {s}</li>)}
                  {(migrateReport.migrations?.applied || []).length === 0 && <li className="text-gray-400">—</li>}
                </ul>
              </div>
              <div>
                <div className="font-bold text-gray-600 mb-1">Already OK ({(migrateReport.migrations?.skipped || []).length})</div>
                <ul className="space-y-0.5">
                  {(migrateReport.migrations?.skipped || []).map((s) => <li key={s} className="text-gray-500">· {s}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-bold text-red-600 mb-1">Failed ({(migrateReport.migrations?.failed || []).length})</div>
                <ul className="space-y-0.5">
                  {(migrateReport.migrations?.failed || []).map((s, i) => (
                    <li key={i} className="text-red-600">✗ {s.step}: {s.error}</li>
                  ))}
                  {(migrateReport.migrations?.failed || []).length === 0 && <li className="text-gray-400">—</li>}
                </ul>
              </div>
            </div>
            {migrateReport.seed && (
              <div className="mt-3 text-gray-700">
                Admin seed: <span className="font-bold">{migrateReport.seed.created ? "created" : migrateReport.seed.resynced ? "password resynced from .env" : "already in sync"}</span>
                {migrateReport.seed.email && <> · <code>{migrateReport.seed.email}</code></>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* DEPLOYMENT PACKAGE TILE */}
      <div className="mt-6 bg-gradient-to-br from-[#FFCC00] via-[#FFE066] to-[#FFCC00] border-2 border-[#1A1A1A] p-6 sm:p-7 relative overflow-hidden" data-testid="deployment-tile">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#E65C00]/20 rounded-full blur-2xl pointer-events-none" aria-hidden />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="flex items-start gap-3 max-w-2xl">
            <div className="w-12 h-12 bg-[#1A1A1A] text-[#FFCC00] flex items-center justify-center shrink-0">
              <Server className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-[#1A1A1A] text-xl tracking-tight">cPanel Deployment Package</div>
              <p className="text-sm text-[#1A1A1A]/80 mt-1.5 leading-relaxed">
                Latest build of your full site bundled for HostRipple cPanel — React frontend + PHP/MySQL backend + database.sql. Click once to download.
              </p>
            </div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/deployment/cpanel-package`}
            download="tara-autohub-cpanel.zip"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#E65C00] text-white px-6 py-3.5 font-heading font-black uppercase tracking-widest text-xs transition-colors shadow-lg"
            data-testid="deployment-download-btn"
          >
            <Download className="w-4 h-4" /> Download cPanel ZIP
          </a>
        </div>
      </div>

      {/* NODE.JS DEPLOYMENT TILE — recommended */}
      <div className="mt-6 bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] text-white border-2 border-[#E65C00] p-6 sm:p-7 relative overflow-hidden" data-testid="deployment-node-tile">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#E65C00]/30 rounded-full blur-2xl pointer-events-none" aria-hidden />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="flex items-start gap-3 max-w-2xl">
            <div className="w-12 h-12 bg-[#E65C00] text-white flex items-center justify-center shrink-0">
              <Server className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-heading font-black uppercase text-white text-xl tracking-tight">Node.js Deployment</div>
                <span className="bg-[#FFCC00] text-[#1A1A1A] text-[10px] font-black uppercase tracking-widest px-2 py-0.5">Recommended</span>
              </div>
              <p className="text-sm text-gray-300 mt-1.5 leading-relaxed">
                Same database, same API, runs as a Passenger Node app on cPanel.
                Fixes the intermittent 503 errors caused by LiteSpeed entry-process limits.
                15-minute setup — see <code className="text-[#FFCC00] bg-black/30 px-1.5 py-0.5">README-NODEJS.md</code> inside the ZIP.
              </p>
            </div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/deployment/node-package`}
            download="tara-autohub-node.zip"
            className="inline-flex items-center gap-2 bg-[#E65C00] hover:bg-[#FFCC00] hover:text-[#1A1A1A] text-white px-6 py-3.5 font-heading font-black uppercase tracking-widest text-xs transition-colors shadow-lg"
            data-testid="deployment-node-download-btn"
          >
            <Download className="w-4 h-4" /> Download Node.js ZIP
          </a>
        </div>
        {/* TAR.GZ fallback — works around cPanel ClamAV false-positives that block .zip with JS */}
        <div className="relative mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-400 max-w-2xl leading-relaxed">
            <span className="font-bold text-[#FFCC00]">Upload blocked by cPanel as a virus?</span> That&apos;s a
            known ClamAV false-positive on <code className="bg-black/30 px-1">.zip</code> files containing JS.
            Use the <code className="bg-black/30 px-1">.tar.gz</code> below — same files, different extension.
            Extract via cPanel File Manager (right-click → Extract).
          </p>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/deployment/node-package-tar`}
            download="tara-autohub-node.tar.gz"
            className="inline-flex items-center gap-2 bg-transparent hover:bg-[#FFCC00] hover:text-[#1A1A1A] text-[#FFCC00] border border-[#FFCC00] px-4 py-2.5 font-heading font-black uppercase tracking-widest text-[10px] transition-colors"
            data-testid="deployment-node-tar-btn"
          >
            <Download className="w-3.5 h-3.5" /> Download .tar.gz instead
          </a>
        </div>
      </div>
    </div>
  );
}
